import { Component, effect, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Access, Role, RoleAccessKey } from '../../../models/rbac';
import { RoleStore } from '../../../stores/role/role.state';

import { InputTextModule } from 'primeng/inputtext';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { CheckboxModule } from 'primeng/checkbox';
import { NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { DividerModule } from 'primeng/divider';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { getState } from '@ngrx/signals';

@Component({
  selector: 'app-role-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    FormFieldWrapperComponent,
    CheckboxModule,
    NgForOf,
    DividerModule,
    FormFooterComponent,
    NgIf,
  ],
  templateUrl: './role-form.component.html',
  styleUrl: './role-form.component.scss',
})
export class RoleFormComponent extends BaseFormComponent<Role> {
  readonly store = inject(RoleStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: Role;
  override formInitialized = false;

  accessList: RoleAccessKey[] = [
    { name: 'lesen', key: 'read' },
    { name: 'bearbeiten', key: 'edit' },
    { name: 'hinzufügen', key: 'add' },
    { name: 'sperren', key: 'lock' },
    { name: 'freigeben', key: 'unlock' },
    { name: 'veröffentlichen', key: 'publish' },
    { name: 'importieren', key: 'import' },
    { name: 'exportieren', key: 'export' },
    { name: 'löschen', key: 'remove' },
  ];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
      }
    });
  }

  /**
   * Initializes the form with the selected item's data and access controls.
   * The form is created using a reactive form group, pre-populated with values from the selected item,
   * including its name and access list permissions.
   *
   * @return {void} No return value.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();
    const accessControls = this.accessList.reduce(
      (acc, curr) => {
        const key = curr.key as keyof Access;
        acc[key] =
          this.itemToEdit && this.itemToEdit.accessList
            ? this.itemToEdit.accessList[key]
            : false;
        return acc;
      },
      {} as Record<keyof Access, boolean>
    );

    this.form = this.fb.group({
      name: [this.itemToEdit?.name || '', Validators.required],
      accessList: this.fb.group(accessControls),
    });
    this.formInitialized = true;
  }

  /**
   * Handles the submission process by invoking the submit method.
   * This method is typically used to trigger submission logic, such as form submission.
   *
   * @return {void} No return value.
   */
  onSubmit(): void {
    this.submit();
  }
}
